// The five round types the practice player can present, one component per
// rung. Each round owns its transient state (picks, checks left, current
// guess), plays its own audio, and reports exactly one result upward via
// `onResolve(correct)` — the player records mastery and advances.
//
// Dial/Fix rounds accept MIDI as an answer method: when `midiEnabled` and the
// active param has a CC binding, turning the physical knob moves the fader,
// and MIDI keys audition the current guess. Touch is always the primary path.

import React, { useCallback, useMemo, useState } from "react";
import { Check, Play, Volume2, X, ArrowRight, BookOpenCheck } from "lucide-react";
import {
  PARAM_BY_ID,
  WAVEFORMS,
  playNote,
  describeChange,
  ccToParamValue,
  audioSupported,
} from "../../lib/synthLab";
import { conceptLabel, dialGrade, nameChoiceLabel, OTHER_ID } from "../../lib/synthLadder";
import { Button } from "../ui/button";
import EnvelopeGraph from "../EnvelopeGraph";
import WaveShape from "./WaveShape";
import BigFader from "./BigFader";
import { useSynthVoices, useMidiInput } from "./useSynthInput";
import { cn } from "../../lib/utils";

const CAN_PLAY = audioSupported();
const ENV_PARAMS = ["attack", "decay", "sustain", "release"];

/* ---------- shared bits ---------- */

const choiceClass = (answered, isRight, isChosen) =>
  cn(
    "flex min-h-[3rem] items-center justify-between rounded-lg border px-3 py-2 text-sm font-medium transition-colors",
    !answered && "border-border bg-background hover:border-primary/60 hover:bg-accent",
    answered && isRight && "border-emerald-400 bg-emerald-50 text-emerald-800",
    answered && isChosen && !isRight && "border-rose-400 bg-rose-50 text-rose-700",
    answered && !isRight && !isChosen && "border-border bg-background opacity-60"
  );

const ChoiceMark = ({ answered, isRight, isChosen }) => {
  if (!answered) return null;
  if (isRight) return <Check className="h-4 w-4" />;
  if (isChosen) return <X className="h-4 w-4" />;
  return null;
};

const FeedbackNext = ({ correct, text, onNext, nextLabel }) => (
  <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
    <p className="text-sm text-muted-foreground">
      <span className={cn("font-medium", correct ? "text-emerald-600" : "text-rose-500")}>
        {correct ? "Nice ear! " : "Not quite — "}
      </span>
      {text}
    </p>
    <Button size="sm" onClick={onNext}>
      {nextLabel}
      <ArrowRight className="h-4 w-4" />
    </Button>
  </div>
);

/* ---------- Hear it — the lesson, inline ---------- */

export const HearRound = ({ ex, onResolve, onNext, nextLabel }) => {
  const { lesson } = ex;
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-primary/70">Hear it</p>
      <h2 className="mt-1 text-lg font-semibold">{lesson.title}</h2>
      <p className="text-sm text-primary/80">{lesson.tagline}</p>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{lesson.body}</p>
      {CAN_PLAY && (
        <>
          <p className="mt-4 text-xs font-medium uppercase tracking-wide text-primary/70">
            {lesson.listen}
          </p>
          <div className={cn("mt-2 grid gap-2", lesson.viz === "wave" ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-3")}>
            {lesson.examples.map((exm) => (
              <button
                key={exm.label}
                type="button"
                onClick={() => playNote(exm.patch, 60, 1100)}
                className="group flex flex-col gap-1 rounded-lg border border-border bg-background px-3 py-2 text-left transition-colors hover:border-primary/60 hover:bg-accent"
              >
                {lesson.viz === "wave" && <WaveShape waveform={exm.patch.waveform} />}
                {lesson.viz === "envelope" && <EnvelopeGraph patch={exm.patch} height={40} />}
                <span className="flex items-center gap-1.5 text-sm font-medium">
                  <Play className="h-3.5 w-3.5 text-primary" />
                  {exm.label}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
      <div className="mt-5 flex justify-end">
        <Button
          size="sm"
          onClick={() => {
            onResolve(true);
            onNext();
          }}
        >
          <BookOpenCheck className="h-4 w-4" />
          Got it — {nextLabel.toLowerCase()}
        </Button>
      </div>
    </div>
  );
};

/* ---------- Name it — which control changed? ---------- */

export const NameRound = ({ ex, onResolve, onNext, nextLabel }) => {
  const [answer, setAnswer] = useState(null);
  const isMystery = ex.answerId === OTHER_ID;

  const pick = (id) => {
    if (answer) return;
    setAnswer(id);
    onResolve(id === ex.answerId);
  };

  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-primary/70">Name it</p>
      <h2 className="mt-1 text-lg font-semibold">Two sounds, one difference.</h2>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => playNote(ex.a, ex.note, ex.gate)} disabled={!CAN_PLAY}>
          <Play className="h-4 w-4" /> Sound A
        </Button>
        <Button size="sm" variant="secondary" onClick={() => playNote(ex.b, ex.note, ex.gate)} disabled={!CAN_PLAY}>
          <Play className="h-4 w-4" /> Sound B
        </Button>
        <span className="text-sm text-muted-foreground">Which control changed?</span>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {ex.choices.map((id) => (
          <button
            key={id}
            type="button"
            disabled={!!answer}
            onClick={() => pick(id)}
            className={choiceClass(!!answer, id === ex.answerId, answer === id)}
          >
            {nameChoiceLabel(id)}
            <ChoiceMark answered={!!answer} isRight={id === ex.answerId} isChosen={answer === id} />
          </button>
        ))}
      </div>
      {answer && (
        <FeedbackNext
          correct={answer === ex.answerId}
          text={
            isMystery
              ? `Between A and B, ${describeChange(ex.a, ex.b, ex.changed)} — that's the ${conceptLabel(
                  ex.changed
                ).toLowerCase()}, a control you'll unlock later. For now, "something else" is the ear's right call.`
              : `Between A and B, ${describeChange(ex.a, ex.b, ex.changed)}. Replay them and listen for it.`
          }
          onNext={onNext}
          nextLabel={nextLabel}
        />
      )}
    </div>
  );
};

/* ---------- Which way — the control is named, call the direction ---------- */

export const WayRound = ({ ex, onResolve, onNext, nextLabel }) => {
  const [answer, setAnswer] = useState(null);

  const pick = (dir) => {
    if (answer) return;
    setAnswer(dir);
    onResolve(dir === ex.direction);
  };

  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-primary/70">Which way</p>
      <h2 className="mt-1 text-lg font-semibold">
        The {conceptLabel(ex.concept).toLowerCase()} moved from A to B — which way?
      </h2>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => playNote(ex.a, ex.note, ex.gate)} disabled={!CAN_PLAY}>
          <Play className="h-4 w-4" /> Sound A
        </Button>
        <Button size="sm" variant="secondary" onClick={() => playNote(ex.b, ex.note, ex.gate)} disabled={!CAN_PLAY}>
          <Play className="h-4 w-4" /> Sound B
        </Button>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {["up", "down"].map((dir) => (
          <button
            key={dir}
            type="button"
            disabled={!!answer}
            onClick={() => pick(dir)}
            className={choiceClass(!!answer, dir === ex.direction, answer === dir)}
          >
            {ex.labels[dir]}
            <ChoiceMark answered={!!answer} isRight={dir === ex.direction} isChosen={answer === dir} />
          </button>
        ))}
      </div>
      {answer && (
        <FeedbackNext
          correct={answer === ex.direction}
          text={`${describeChange(ex.a, ex.b, ex.concept)}.`}
          onNext={onNext}
          nextLabel={nextLabel}
        />
      )}
    </div>
  );
};

/* ---------- shared dial mechanics (Dial it + the fix stage of Fix it) ---------- */

// Big wave picker used when the "knob" is the waveform.
const WavePick = ({ value, onChange, disabled }) => (
  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
    {WAVEFORMS.map((w) => (
      <button
        key={w.value}
        type="button"
        disabled={disabled}
        onClick={() => onChange(w.value)}
        className={cn(
          "flex flex-col gap-1 rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors",
          value === w.value
            ? "border-primary bg-primary/10"
            : "border-border bg-background hover:border-primary/60 hover:bg-accent",
          disabled && "opacity-60"
        )}
      >
        <WaveShape waveform={w.value} />
        {w.label}
      </button>
    ))}
  </div>
);

// Everything shared between "set this knob by ear" surfaces: the guess state,
// the check budget, MIDI-as-answer, and the target/mine audition buttons.
const useDialSurface = ({ ex, targetPatch, minePatchOf, midiEnabled, ccMap, onDone }) => {
  const [guess, setGuess] = useState(ex.kind === "fix" ? ex.start[ex.concept] : ex.startValue);
  const [checksLeft, setChecksLeft] = useState(ex.checksAllowed);
  const [hint, setHint] = useState(null);
  const [done, setDone] = useState(null); // { pass, firstTry }

  const minePatch = useMemo(() => minePatchOf(guess), [guess, minePatchOf]);
  const targetValue = ex.kind === "fix" ? ex.target[ex.concept] : ex.targetValue;

  // MIDI: notes audition the current guess; a bound CC turns the active knob.
  const { press, release } = useSynthVoices(minePatch);
  const onControl = useCallback(
    (controller, value) => {
      if (done) return;
      if (ex.concept !== "waveform" && ccMap && ccMap[ex.concept] === controller) {
        setGuess(ccToParamValue(ex.concept, value));
        setHint(null);
      }
    },
    [done, ex.concept, ccMap]
  );
  useMidiInput(press, release, !!midiEnabled, onControl);

  const check = () => {
    if (done) return;
    const g = dialGrade(ex.concept, guess, targetValue);
    const firstTry = checksLeft === ex.checksAllowed;
    if (g.pass) {
      const d = { pass: true, firstTry };
      setDone(d);
      onDone(d);
      return;
    }
    const left = checksLeft - 1;
    setChecksLeft(left);
    if (left <= 0) {
      setGuess(targetValue); // reveal, so the player can hear the real answer
      const d = { pass: false, firstTry: false };
      setDone(d);
      onDone(d);
    } else {
      setHint(g.hint);
    }
  };

  const update = (v) => {
    if (done) return;
    setGuess(v);
    setHint(null);
  };

  return { guess, update, check, checksLeft, hint, done, minePatch, targetPatch, targetValue };
};

const DialSurface = ({ ex, surface, prompt }) => {
  const { guess, update, check, checksLeft, hint, done, minePatch, targetPatch } = surface;
  const isWave = ex.concept === "waveform";
  return (
    <div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => playNote(targetPatch, ex.note, ex.gate)} disabled={!CAN_PLAY}>
          <Volume2 className="h-4 w-4" /> {ex.kind === "fix" ? "Original" : "Target"}
        </Button>
        <Button size="sm" variant="secondary" onClick={() => playNote(minePatch, ex.note, ex.gate)} disabled={!CAN_PLAY}>
          <Play className="h-4 w-4" /> Mine
        </Button>
        <span className="text-sm text-muted-foreground">{prompt}</span>
      </div>

      <div className="mt-4">
        {isWave ? (
          <WavePick value={guess} onChange={update} disabled={!!done} />
        ) : (
          <BigFader param={PARAM_BY_ID[ex.concept]} value={guess} onChange={update} disabled={!!done} />
        )}
      </div>

      {!isWave && ENV_PARAMS.includes(ex.concept) && (
        <div className="mt-3 rounded-lg border border-border bg-background/60 px-3 py-2">
          <EnvelopeGraph patch={minePatch} height={64} />
        </div>
      )}

      {!done && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button size="sm" onClick={check}>
            <Check className="h-4 w-4" />
            Check · {checksLeft} left
          </Button>
          {hint && <span className="text-sm font-medium text-amber-600">Try {hint}.</span>}
        </div>
      )}
    </div>
  );
};

/* ---------- Dial it — hear the target, set the knob by ear ---------- */

export const DialRound = ({ ex, midiEnabled, ccMap, onResolve, onNext, nextLabel }) => {
  const targetPatch = useMemo(
    () => ({ ...ex.context, [ex.concept]: ex.targetValue }),
    [ex]
  );
  const minePatchOf = useCallback((g) => ({ ...ex.context, [ex.concept]: g }), [ex]);
  const surface = useDialSurface({
    ex,
    targetPatch,
    minePatchOf,
    midiEnabled,
    ccMap,
    // Stars come from first-check precision; later checks still finish the round.
    onDone: (d) => onResolve(d.pass && d.firstTry),
  });
  const label = conceptLabel(ex.concept).toLowerCase();

  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-primary/70">Dial it</p>
      <h2 className="mt-1 text-lg font-semibold">Match the target — {label} only.</h2>
      <DialSurface
        ex={ex}
        surface={surface}
        prompt={`Set the ${label} until Mine sounds like Target.`}
      />
      {surface.done && (
        <FeedbackNext
          correct={surface.done.pass && surface.done.firstTry}
          text={
            surface.done.pass
              ? surface.done.firstTry
                ? "Nailed it on the first check."
                : "Got there — a first-check hit is what earns the star."
              : `The target was ${
                  ex.concept === "waveform"
                    ? (WAVEFORMS.find((w) => w.value === surface.targetValue) || {}).label
                    : PARAM_BY_ID[ex.concept].format(surface.targetValue)
                } — it's set now, play Mine to hear it.`
          }
          onNext={onNext}
          nextLabel={nextLabel}
        />
      )}
    </div>
  );
};

/* ---------- Fix it — a preset with one control wrecked ---------- */

export const FixRound = ({ ex, midiEnabled, ccMap, onResolve, onNext, nextLabel }) => {
  // Stage 1 (capstone only): diagnose which control is off before touching it.
  const [pick, setPick] = useState(null);
  const [stage, setStage] = useState(ex.reveal ? "dial" : "identify");
  const identifyOk = ex.reveal ? true : pick === ex.concept;

  const targetPatch = useMemo(() => ({ ...ex.target }), [ex]);
  const minePatchOf = useCallback((g) => ({ ...ex.start, [ex.concept]: g }), [ex]);
  const surface = useDialSurface({
    ex,
    targetPatch,
    minePatchOf,
    midiEnabled,
    ccMap,
    // A fix is "correct" when the diagnosis was right AND the repair landed
    // within the check budget.
    onDone: (d) => onResolve(identifyOk && d.pass),
  });
  const label = conceptLabel(ex.concept).toLowerCase();

  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-primary/70">Fix the patch</p>
      <h2 className="mt-1 text-lg font-semibold">
        {ex.presetEmoji} {ex.presetName} — one control got wrecked.
      </h2>

      {stage === "identify" ? (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => playNote(targetPatch, ex.note, ex.gate)} disabled={!CAN_PLAY}>
              <Volume2 className="h-4 w-4" /> Original
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => playNote(minePatchOf(ex.start[ex.concept]), ex.note, ex.gate)}
              disabled={!CAN_PLAY}
            >
              <Play className="h-4 w-4" /> Wrecked
            </Button>
            <span className="text-sm text-muted-foreground">Which control is off?</span>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {ex.choices.map((id) => (
              <button
                key={id}
                type="button"
                disabled={pick != null}
                onClick={() => setPick(id)}
                className={choiceClass(pick != null, id === ex.concept, pick === id)}
              >
                {conceptLabel(id)}
                <ChoiceMark answered={pick != null} isRight={id === ex.concept} isChosen={pick === id} />
              </button>
            ))}
          </div>
          {pick != null && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                {identifyOk ? "Right — " : `It was the ${label} — `}
                now repair it by ear.
              </p>
              <Button size="sm" onClick={() => setStage("dial")}>
                Fix it
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <DialSurface
            ex={ex}
            surface={surface}
            prompt={`Restore the ${label} until it matches the original.`}
          />
          {surface.done && (
            <FeedbackNext
              correct={identifyOk && surface.done.pass}
              text={
                surface.done.pass
                  ? identifyOk
                    ? "Patch restored."
                    : "Repaired — but the diagnosis is half the skill."
                  : `It should sit at ${
                      ex.concept === "waveform"
                        ? (WAVEFORMS.find((w) => w.value === surface.targetValue) || {}).label
                        : PARAM_BY_ID[ex.concept].format(surface.targetValue)
                    } — it's restored now, compare Original and Mine.`
              }
              onNext={onNext}
              nextLabel={nextLabel}
            />
          )}
        </>
      )}
    </div>
  );
};
